import Table from "@components/vendor/dashboard/table";
import { TTableColumns } from "@components/vendor/dashboard/table/types";
import { ActionIcon } from "@mantine/core";
import RoutesInstance from "@routes/instances";
import { useMutation } from "@tanstack/react-query";
import Common from "@utilities/common";
import DateHelper from "@utilities/date";
import { FC } from "react";
import { BiEdit } from "react-icons/bi";
import { IoMdRemoveCircle } from "react-icons/io";
import {
    Link,
    useLocation,
    useNavigate,
    useSearchParams,
} from "react-router-dom";
import Filters from "./filters";

interface Props {
    data: Array<IGallery["get"]>;
    pagination: Required<IReturnValue["pagination"]>;
    loading: boolean;
}

const GalleryContainer: FC<Props> = ({ data, loading, pagination }) => {
    const { pathname } = useLocation();

    const [URLSearchParams] = useSearchParams();

    const Navigate = useNavigate();

    const { mutate, isLoading } = useMutation(
        ["removeGalleryItem"],
        (id: number) =>
            Common.api.gallery.Delete({
                id: id,
            }),
        {
            onSuccess: () => {
                URLSearchParams.set("refetch", "true");

                Navigate(
                    {
                        pathname,
                        search: "?" + URLSearchParams.toString(),
                    },
                    {
                        replace: true,
                    },
                );
            },
        },
    );

    const handleRemove = (id: number) => {
        mutate(id);
    };

    const columns: TTableColumns<IGallery["get"]> = [
        {
            id: "file",
            accessorKey: "تصویر",
            cell: (data) => (
                <div className="rtl:font-numeric">
                    {data.row.original.file &&
                    data.row.original.type === "image" ? (
                        <img
                            width={128}
                            src={data.row.original.file}
                            alt={data.row.original.title}
                        />
                    ) : (
                        <div className="w-12">-</div>
                    )}
                </div>
            ),
        },
        {
            id: "title",
            accessorKey: "عنوان",
            cell: (data) => (
                <div className="rtl:font-numeric">
                    {data.row.original.title}
                </div>
            ),
        },
        {
            id: "type",
            accessorKey: "نوع",
            cell: (data) => (
                <div className="rtl:font-numeric">
                    {data.row.original.type === "video" ? "ویدیو" : "عکس"}
                </div>
            ),
        },
        {
            id: "createDateTime",
            accessorKey: "زمان ساخت",
            cell: (data) => (
                <div className="rtl:font-numeric">
                    {DateHelper({
                        datetime: data.row.original.createDateTime,
                        format: "DD MMMM YYYY - HH:mm",
                    })}
                </div>
            ),
        },
        {
            accessorKey: "عملیات",
            cell: (data) => (
                <div className="flex items-center flex-wrap">
                    <Link
                        to={RoutesInstance.editGallery.replace(
                            ":id",
                            data.row.original.id.toString(),
                        )}>
                        <ActionIcon
                            variant="subtle"
                            color="yellow"
                            radius={9999}
                            className="mx-1"
                            size="lg"
                            disabled={isLoading}>
                            <BiEdit className="text-2xl" />
                        </ActionIcon>
                    </Link>

                    <ActionIcon
                        variant="subtle"
                        color="red"
                        radius={9999}
                        className="mx-1"
                        size="lg"
                        loading={isLoading}
                        disabled={isLoading}
                        onClick={() => handleRemove(data.row.original.id)}>
                        <IoMdRemoveCircle className="text-2xl" />
                    </ActionIcon>
                </div>
            ),
        },
    ];

    return (
        <>
            <Filters />

            <Table<IGallery["get"]>
                columns={columns}
                data={data}
                loading={loading}
                options={{
                    rowNumber: true,
                }}
                pagination={{
                    activePage: pagination?.page || 0,
                    isStartFromZero: false,
                    lastPage: pagination
                        ? Math.ceil(pagination?.total / pagination?.limit)
                        : 1,
                    queryKey: "page",
                }}
            />
        </>
    );
};

export default GalleryContainer;
